import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["link", "section"]
  static values = {
    offset: { type: Number, default: 112 },
    activeClass: String,
    inactiveClass: String
  }

  connect() {
    this.onScroll = this.onScroll.bind(this)
    this.setActiveFromHash()
    this.updateActiveLink()

    window.addEventListener("scroll", this.onScroll, { passive: true })
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
      this.animationFrame = null
    }
  }

  scroll(event) {
    event.preventDefault()

    const id = this.idFromLink(event.currentTarget)
    if (!id) return

    const section = this.findSectionById(id)
    if (!section) return

    const top = window.scrollY + section.getBoundingClientRect().top - this.offsetValue
    window.scrollTo({ top, behavior: "smooth" })
    history.replaceState(null, "", `#${id}`)
    this.setActiveId(id)
  }

  onScroll() {
    if (this.animationFrame) return

    this.animationFrame = requestAnimationFrame(() => {
      this.animationFrame = null
      this.updateActiveLink()
    })
  }

  updateActiveLink() {
    if (!this.hasLinkTarget) return

    const sections = this.sections()
    if (!sections.length) return

    const threshold = this.offsetValue + 16
    let activeSectionId = sections[0]?.id

    sections.forEach((section) => {
      if (section.getBoundingClientRect().top <= threshold) {
        activeSectionId = section.id
      }
    })

    if (activeSectionId) {
      this.setActiveId(activeSectionId)
    }
  }

  setActiveFromHash() {
    const hashId = window.location.hash.replace("#", "")
    if (!hashId) return
    if (!this.findSectionById(hashId)) return

    this.setActiveId(hashId)
  }

  setActiveId(id) {
    if (this.activeId === id) return

    this.activeId = id
    const activeClasses = this.cssClassesFromValue(this.activeClassValue)
    const inactiveClasses = this.cssClassesFromValue(this.inactiveClassValue)

    this.linkTargets.forEach((link) => {
      const matches = this.idFromLink(link) === id
      link.classList.toggle("is-active", matches)
      activeClasses.forEach((cssClass) => link.classList.toggle(cssClass, matches))
      inactiveClasses.forEach((cssClass) => link.classList.toggle(cssClass, !matches))

      if (matches) {
        link.setAttribute("aria-current", "true")
      } else {
        link.removeAttribute("aria-current")
      }
    })
  }

  findSectionById(id) {
    return this.sections().find((section) => section.id === id)
  }

  idFromLink(linkElement) {
    if (!linkElement) return null
    const href = linkElement.getAttribute("href") || ""
    if (!href.startsWith("#")) return null

    return href.slice(1)
  }

  cssClassesFromValue(value) {
    return (value || "").split(/\s+/).filter(Boolean)
  }

  sections() {
    if (this.hasSectionTarget) return this.sectionTargets

    // Fallback for server-rendered article content if target attributes are missing/stripped.
    return Array.from(this.element.querySelectorAll("h2[id], [data-blog-toc-target='section']"))
  }
}
